// Custom hook for inter-window communication
import { useCallback, useRef } from "react";
import { ExternalRecordingService } from "../services/externalRecordingService";
import {
  initBackupSession,
  ensureBackupSession,
  updateBackupSessionPathInfo,
  startPeriodicBackup,
  stopPeriodicBackup,
  saveTranscriptionBackup,
  ensureBackupBucket,
  endBackupSession,
  getBackupStatus,
  getLatestBackup,
} from "../utils/transcriptionBackupUtils";
import { getSupabase } from "../../../components/supabaseClient";
import { getBackendURL } from "../../../components/env";
import { tableForPatientId } from "../../medstudent/chartSource";
import { generateClinicalTabsAndPersistCopilotSnapshot } from "../utils/copilotBackupUtils";
import {
  getUserSchema,
  getPatientBackupPathHintsForExternalRecording,
  appendLiveTranscriptEntry,
} from "../utils/databaseUtils";
import { resolveRecorderSchema } from "../../../utils/recorderSchema";

function mergeDbPathHintsIntoPathInfo(pathInfo, hints) {
  if (!pathInfo || !hints) return pathInfo;
  const fromDb =
    hints.rawPatientName != null &&
    String(hints.rawPatientName).trim() !== ""
      ? String(hints.rawPatientName).trim()
      : null;
  return {
    ...pathInfo,
    schemaName: hints.schemaName,
    year: hints.year,
    date: hints.date,
    ehrId: hints.ehrId,
    ...(fromDb ? { patientName: fromDb } : {}),
  };
}

async function loadExternalRecordingPathHints(data, state) {
  const callerDate =
    data.scheduledDate ||
    state?.date_ ||
    state?.scheduledDate ||
    state?.date ||
    null;
  try {
    return await getPatientBackupPathHintsForExternalRecording(
      data.patientId,
      callerDate,
      data.patientName || data.patient_name || "",
      data.ehrId || data.ehr_id || "",
    );
  } catch (e) {
    console.warn("⚠️ [BACKUP] loadExternalRecordingPathHints failed:", e);
    return null;
  }
}

// Helper function to get current audio count for a patient (copied pattern from databaseUtils.js)
const getCurrentAudioCountForBackup = async (
  supabase,
  patientId,
  tableName,
) => {
  try {
    const { data, error } = await supabase
      .from(tableForPatientId(patientId))
      .select("more_audios")
      .eq("id", patientId)
      .single();

    if (error) {
      console.log(
        "No existing audio records found for backup, starting with 0",
      );
      return 0;
    }

    const audioCount = data?.more_audios?.length || 0;
    console.log(`Current audio count for patient ${patientId}: ${audioCount}`);
    return audioCount;
  } catch (error) {
    console.warn("Error fetching audio count for backup:", error);
    return 0;
  }
};

// Helper function to get the schema name
const getUserSchemaForBackup = async (supabase) => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return "public";
    const { data: userData } = await supabase
      .from("users")
      .select("schema")
      .eq("id", user.id)
      .single();
    const supabaseSchema = userData?.schema || "public";
    return supabaseSchema || "public";
  } catch (error) {
    console.warn("Error fetching user schema:", error);
    return "public";
  }
};

// Helper function to determine if an error is retryable
const isRetryableError = (errorMessage) => {
  if (!errorMessage) return false;
  
  // Network-related errors that might succeed on retry
  const retryablePatterns = [
    'network',
    'timeout',
    'ECONNRESET',
    'ETIMEDOUT',
    'ENOTFOUND',
    'fetch failed',
    'Failed to fetch',
    'network error',
    'connection',
    'temporarily unavailable',
    'service unavailable',
    '429',
    '503',
    '502',
  ];
  
  const lowerError = errorMessage.toLowerCase();
  return retryablePatterns.some(pattern => lowerError.includes(pattern.toLowerCase()));
};

export const useWindowMessaging = ({
  onAudioSaved,
  bucketName,
  tableName,
  supabaseUrl,
  state,
}) => {
  const saveLiveTranscriptDirectly = useCallback(
    async ({
      patientId,
      patientName,
      transcriptText,
      duration,
      audioUrl,
      sessionId,
    }) => {
      try {
        const appendResult = await appendLiveTranscriptEntry({
          patientId,
          patientName: patientName || state?.patient_name,
          transcriptText,
          duration,
          audioUrl,
          sessionId,
        });
        if (!appendResult.success) {
          return { success: false, error: appendResult.error || "Save failed" };
        }
        if (appendResult.skipped) {
          return { success: true, skipped: true };
        }
        const entry = appendResult.entry;
        const resolvedAudioUrl = entry.audio_url;
        const supabase = await getSupabase();

        // Check if user has copilot_enabled set to true and run AI auto-generation
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            const { data: userData } = await supabase
              .from("users")
              .select("copilot_enabled")
              .eq("id", session.user.id)
              .maybeSingle();

            // TEMP 2026-06-25 — paywall bypass for dogfood (Task #168 to restore).
            // ORIGINAL: if (userData?.copilot_enabled === true) {
            if (true) {
              const baseUrl = getBackendURL();
              if (resolvedAudioUrl && transcriptText?.trim()) {
                generateClinicalTabsAndPersistCopilotSnapshot({
                  transcriptText: transcriptText.trim(),
                  backendBaseUrl: baseUrl,
                  audioRow: null,
                  transcriptRow: entry,
                  audioPublicUrl: resolvedAudioUrl,
                  userId: session.user.id,
                  userJwtToken: session.access_token,
                  patientId: patientId,
                  sessionId: sessionId,
                }).then((res) => {
                  if (res.ok) {
                    console.log("✅ [PARENT-WINDOW] Live transcript auto-generated Copilot tabs:", res);
                    window.dispatchEvent(new CustomEvent("copilotTabsGenerated"));
                  } else {
                    console.warn("⚠️ [PARENT-WINDOW] Copilot auto-generation failed:", res.error);
                  }
                });
              }
            }
          }
        } catch (copilotErr) {
          console.error("❌ [PARENT-WINDOW] Error in Copilot auto-generation:", copilotErr);
        }

        return { success: true };
      } catch (error) {
        console.error(
          "❌ [PARENT-WINDOW] Failed direct live transcript save:",
          error,
        );
        return { success: false, error: error?.message || "Save failed" };
      }
    },
    [state],
  );

  const createStandardizedFilename = (data) => {
    return ExternalRecordingService.createStandardizedFilename(data);
  };

  // Ref to store current transcription data - updated by LIVE_TRANSCRIPT_UPDATE messages
  const transcriptionDataRef = useRef({
    transcriptText: "",
    duration: 0,
    recordingState: "idle",
    sessionId: null,
    capturedAt: 0,
  });

  // Track current recording session for transcription backups
  const currentTranscriptionSession = useCallback(
    async (data) => {
      // Initialize backup session when recording starts
      if (data.sessionId && data.patientId) {
        const scheduledDate =
          data.scheduledDate ||
          state?.date_ ||
          state?.scheduledDate ||
          state?.date ||
          "";

        let year = scheduledDate
          ? scheduledDate.split("-")[0]
          : new Date().getFullYear().toString();
        let date = scheduledDate || new Date().toISOString().split("T")[0];

        const fallbackSchema =
          data.schemaName ||
          data.schema_name ||
          (state?.schema
            ? state.schema.includes(",")
              ? state.schema.split(",")[0].trim()
              : state.schema
            : "public");

        // Register the session synchronously with what we already know so the
        // first periodic ticks / an early pause / an early stop are attributed
        // to it; the DB-derived folder + EHR are patched in below once known.
        initBackupSession(data.sessionId, data.patientId, {
          schemaName: fallbackSchema,
          year,
          date,
          patientName: data.patientName || data.patient_name || "",
          ehrId: data.ehrId || data.ehr_id || "unknown_ehr",
          audioNumber: data.audioNumber ?? data.audio_number ?? 1,
        });

        const dbHints = await loadExternalRecordingPathHints(data, state);

        let schemaName = "public";
        try {
          schemaName = await getUserSchema();
          console.log("💾 [BACKUP] Got user schema:", schemaName);
        } catch (schemaError) {
          console.warn(
            "⚠️ Could not get user schema, using fallback:",
            schemaError,
          );
          schemaName = fallbackSchema;
        }

        if (dbHints) {
          schemaName = dbHints.schemaName;
          year = dbHints.year;
          date = dbHints.date;
        }

        let audioNumber = data.audioNumber ?? data.audio_number ?? 1;
        try {
          const supabase = await getSupabase();
          const table = tableForPatientId(data.patientId) || "audio_notes";
          const currentAudioCount = await getCurrentAudioCountForBackup(
            supabase,
            data.patientId,
            table,
          );
          audioNumber = currentAudioCount + 1;
          console.log(
            `💾 [BACKUP] Calculated audio number: ${audioNumber} (current count: ${currentAudioCount})`,
          );
        } catch (error) {
          console.warn(
            "⚠️ Could not calculate audio number, defaulting to 1:",
            error,
          );
        }

        const pathInfo = mergeDbPathHintsIntoPathInfo(
          {
            schemaName,
            year,
            date,
            patientName: data.patientName || data.patient_name || "",
            ehrId: data.ehrId || data.ehr_id || "unknown_ehr",
            audioNumber,
          },
          dbHints,
        );
        // No-op if this session was stopped/replaced while we were resolving.
        updateBackupSessionPathInfo(data.sessionId, pathInfo);
      }
    },
    [state],
  );

  /**
   * Path info for a message that arrives without a registered session (parent
   * remounted mid-recording, or the popup's final tick after RECORDING_STOPPED).
   * When the session is already registered the stored path info is reused and
   * the DB lookups are skipped.
   */
  const resolvePathInfoForMessage = useCallback(
    async (data) => {
      const status = getBackupStatus();
      if (status?.sessionId === data.sessionId && status?.pathInfo) {
        return status.pathInfo;
      }
      let pathSchemaName = "public";
      try {
        pathSchemaName = await getUserSchema();
      } catch (e) {
        pathSchemaName =
          data.schemaName ||
          data.schema_name ||
          (state?.schema
            ? state.schema.includes(",")
              ? state.schema.split(",")[0].trim()
              : state.schema
            : "public");
      }
      const dbHints = await loadExternalRecordingPathHints(data, state);
      const scheduled = data.scheduledDate || data.date || null;
      return mergeDbPathHintsIntoPathInfo(
        {
          schemaName: pathSchemaName,
          year:
            data.year ||
            (scheduled ? String(scheduled).slice(0, 4) : null) ||
            new Date().getFullYear().toString(),
          date: scheduled || new Date().toISOString().split("T")[0],
          patientName: data.patientName || data.patient_name || "",
          ehrId: data.ehrId || data.ehr_id || "unknown_ehr",
          audioNumber: data.audioNumber ?? data.audio_number ?? 1,
        },
        dbHints,
      );
    },
    [state],
  );

  /**
   * Queue a transcript snapshot from a popup message for backup.
   * Handles ABRUPTLY_CLOSED / PAUSED / STOPPED / periodic ticks uniformly.
   */
  const backupTranscriptFromMessage = useCallback(
    async (data, transcriptText, recordingState) => {
      if (!transcriptText || !String(transcriptText).trim() || !data.sessionId) {
        return { success: false, error: "No transcription data" };
      }
      const pathInfo = await resolvePathInfoForMessage(data);
      ensureBackupSession(data.sessionId, data.patientId, pathInfo);
      return saveTranscriptionBackup({
        sessionId: data.sessionId,
        patientId: data.patientId,
        transcriptText: String(transcriptText),
        duration: data.duration || 0,
        recordingState,
        timestamp: data.timestamp || new Date().toISOString(),
        capturedAt: data.capturedAt || data.timestamp || Date.now(),
        pathInfo,
        metadata: {
          recording_state: recordingState,
          source: `popup_${recordingState}`,
          session_id: data.sessionId,
        },
      });
    },
    [resolvePathInfoForMessage],
  );

  const handleExternalAudioComplete = useCallback(
    async (event) => {
      // `let`: the AUDIO_RECORDING_COMPLETE branch patches a missing patientId in.
      let data = event.data;

      console.log("🎤 [PARENT-WINDOW] Received message event:", {
        type: data?.type,
        hasAudioBlob: !!data?.audioBlob,
        patientId: data?.patientId,
        source: event.source ? "external-window" : "unknown",
      });

      // Handle recording started - initialize transcription backup session.
      // Also sent (continuation: true) for each new segment after Save & continue.
      if (data.type === "RECORDING_STARTED") {
        console.log(
          "💾 [PARENT-WINDOW] Recording started - initializing backup session",
          { sessionId: data?.sessionId, continuation: !!data?.continuation },
        );

        if (data.sessionId && data.patientId) {
          // Registers the session synchronously, then refines path info async
          // (the refinement is a no-op if the session was stopped meanwhile).
          currentTranscriptionSession(data).catch((err) => {
            console.warn(
              "⚠️ [PARENT-WINDOW] Backup session path resolution failed:",
              err,
            );
          });

          // Reset the snapshot for the new session so a stale snapshot from the
          // previous segment is never written under this session's file.
          transcriptionDataRef.current = {
            transcriptText: data.liveTranscriptText || "",
            duration: data.duration || 0,
            recordingState: "recording",
            sessionId: data.sessionId,
            capturedAt: Date.now(),
          };

          // Start periodic transcription backup - reads from ref for live updates
          const getTranscriptionData = () => transcriptionDataRef.current;
          startPeriodicBackup(getTranscriptionData, data.patientId);
          console.log(
            "💾 [PARENT-WINDOW] Started periodic transcription backup",
          );
        }

        // Also ensure backup bucket exists
        ensureBackupBucket();
      }

      // Handle recording stopped - persist the final transcript, then end the session
      if (data.type === "RECORDING_STOPPED") {
        console.log(
          "💾 [PARENT-WINDOW] Recording stopped - saving final transcript backup and ending session",
          {
            sessionId: data?.sessionId,
            finalTranscriptLength: data?.finalTranscript?.length || 0,
          },
        );
        // Only stop the interval when it belongs to this session; a newer
        // segment (Save & continue) may already own it.
        if (getBackupStatus()?.sessionId === data.sessionId) {
          stopPeriodicBackup();
        }

        const finalText =
          (data.finalTranscript && String(data.finalTranscript).trim()) ||
          (transcriptionDataRef.current?.sessionId === data.sessionId
            ? transcriptionDataRef.current.transcriptText
            : "");
        if (finalText && data.sessionId) {
          try {
            const finalResult = await backupTranscriptFromMessage(
              data,
              finalText,
              "stopped",
            );
            if (!finalResult.success) {
              console.warn(
                "💾 [PARENT-WINDOW] Final transcription backup failed:",
                finalResult.error,
              );
            }
          } catch (finalErr) {
            console.error(
              "❌ [PARENT-WINDOW] Error saving final transcription backup:",
              finalErr,
            );
          }
        }

        // A later RECORDING_STARTED may already own the session (Save & continue).
        if (getBackupStatus()?.sessionId === data.sessionId) {
          const sessionStatus = endBackupSession();
          console.log("💾 [PARENT-WINDOW] Backup session ended:", sessionStatus);
        }
      }

      // Handle recording abruptly closed - save final transcription backup before cleanup
      if (data.type === "RECORDING_ABRUPTLY_CLOSED") {
        console.log(
          "💾 [PARENT-WINDOW] Recording window closed abruptly - saving emergency transcription backup",
          {
            sessionId: data?.sessionId,
            patientId: data?.patientId,
            transcriptLength: data?.transcriptText?.length || 0,
            duration: data?.duration,
          },
        );
        if (getBackupStatus()?.sessionId === data.sessionId) {
          stopPeriodicBackup();
        }

        // Save the transcription even if window is closing
        const abruptText =
          (data.transcriptText && String(data.transcriptText).trim()) ||
          (transcriptionDataRef.current?.sessionId === data.sessionId
            ? transcriptionDataRef.current.transcriptText
            : "");
        if (abruptText && data.sessionId) {
          try {
            const transcriptionResult = await backupTranscriptFromMessage(
              data,
              abruptText,
              "abruptly_closed",
            );

            if (transcriptionResult.success) {
              console.log(
                "💾 [PARENT-WINDOW] Emergency transcription backup saved:",
                transcriptionResult,
              );
            } else {
              console.warn(
                "💾 [PARENT-WINDOW] Emergency transcription backup failed:",
                transcriptionResult.error,
              );
            }
          } catch (transcriptionError) {
            console.error(
              "❌ [PARENT-WINDOW] Error saving emergency transcription backup:",
              transcriptionError,
            );
          }
        }

        // End the backup session
        if (getBackupStatus()?.sessionId === data.sessionId) {
          const sessionStatus = endBackupSession();
          console.log(
            "💾 [PARENT-WINDOW] Backup session ended after abrupt close:",
            sessionStatus,
          );
        }
      }

      // Handle recording paused - save current transcription backup
      if (data.type === "RECORDING_PAUSED") {
        console.log(
          "💾 [PARENT-WINDOW] Recording paused - saving transcription backup",
          {
            sessionId: data?.sessionId,
            transcriptLength: data?.transcriptText?.length || 0,
            duration: data?.duration,
          },
        );

        if (data.transcriptText && data.sessionId) {
          // Keep the periodic snapshot in step with the popup's canonical text.
          if (transcriptionDataRef.current?.sessionId === data.sessionId) {
            transcriptionDataRef.current = {
              ...transcriptionDataRef.current,
              transcriptText: data.transcriptText,
              duration: data.duration || transcriptionDataRef.current.duration,
              recordingState: "paused",
              capturedAt: data.timestamp
                ? new Date(data.timestamp).getTime() || Date.now()
                : Date.now(),
            };
          }
          try {
            const transcriptionResult = await backupTranscriptFromMessage(
              data,
              data.transcriptText,
              "paused",
            );

            if (transcriptionResult.success) {
              console.log(
                "💾 [PARENT-WINDOW] Transcription backup saved on pause:",
                transcriptionResult,
              );
            } else {
              console.warn(
                "💾 [PARENT-WINDOW] Transcription backup failed on pause:",
                transcriptionResult.error,
              );
            }
          } catch (transcriptionError) {
            console.error(
              "❌ [PARENT-WINDOW] Error saving transcription backup on pause:",
              transcriptionError,
            );
          }
        }
      }

      // Handle recording resumed. The popup still holds its own transcript; the
      // backup is only pushed back when it is for THIS session and holds more
      // text than the parent last saw (i.e. the popup lost state), never to
      // replace a longer in-memory transcript with an older snapshot.
      if (data.type === "RECORDING_RESUMED") {
        console.log(
          "💾 [PARENT-WINDOW] Recording resumed",
          {
            sessionId: data?.sessionId,
            patientId: data?.patientId,
          },
        );

        // The popup reports what it currently holds; keep the parent's
        // snapshot current before comparing against the stored backup.
        if (
          data.sessionId &&
          data.transcriptText &&
          transcriptionDataRef.current?.sessionId === data.sessionId
        ) {
          transcriptionDataRef.current = {
            ...transcriptionDataRef.current,
            transcriptText: data.transcriptText,
            capturedAt: data.timestamp
              ? new Date(data.timestamp).getTime() || Date.now()
              : Date.now(),
          };
        }

        if (data.sessionId && data.patientId) {
          try {
            const latestBackup = await getLatestBackup(
              data.patientId,
              data.sessionId,
            );
            const backupSession =
              latestBackup?.session_id || latestBackup?.metadata?.session_id;
            const backupText = latestBackup?.transcript_text || "";
            const knownText =
              transcriptionDataRef.current?.sessionId === data.sessionId
                ? transcriptionDataRef.current.transcriptText || ""
                : "";

            if (
              backupText.trim() &&
              backupSession === data.sessionId &&
              backupText.length > knownText.length
            ) {
              console.log(
                "💾 [PARENT-WINDOW] Found newer transcription backup, sending to external window:",
                {
                  transcriptLength: backupText.length,
                  duration: latestBackup.duration,
                },
              );
              if (event.source && typeof event.source.postMessage === "function") {
                event.source.postMessage(
                  {
                    type: "TRANSCRIPTION_RESTORED",
                    transcriptText: backupText,
                    duration: latestBackup.duration,
                    sessionId: data.sessionId,
                  },
                  "*",
                );
              }
              transcriptionDataRef.current = {
                transcriptText: backupText,
                duration: latestBackup.duration || 0,
                recordingState: "recording",
                sessionId: data.sessionId,
                capturedAt: Date.now(),
              };
            } else {
              console.log(
                "💾 [PARENT-WINDOW] No newer transcription backup to restore",
              );
            }
          } catch (restoreError) {
            console.error(
              "❌ [PARENT-WINDOW] Error restoring transcription backup:",
              restoreError,
            );
          }
        }
        if (transcriptionDataRef.current?.sessionId === data.sessionId) {
          transcriptionDataRef.current.recordingState = "recording";
        }
      }

      if (data.type === "AUDIO_RECORDING_COMPLETE") {
        // Validate patientId before processing
        if (!data.patientId) {
          console.error("❌ [PARENT-WINDOW] Missing patientId in AUDIO_RECORDING_COMPLETE message");
          // Try to get patientId from state as fallback
          const statePatientId = state?.id || state?.patientId;
          if (statePatientId) {
            console.log("⚠️ [PARENT-WINDOW] Using fallback patientId from state:", statePatientId);
            data = { ...data, patientId: statePatientId };
          } else {
            // Dispatch error notification
            if (onAudioSaved) {
              onAudioSaved({
                success: false,
                error: "Missing patient ID - cannot save audio",
                details: { message: "patientId is required but was not provided" },
              });
            }
            return;
          }
        }

        // Validate audio blob exists
        if (!data.audioBlob || data.audioBlob.size === 0) {
          console.error("❌ [PARENT-WINDOW] Missing or empty audioBlob in AUDIO_RECORDING_COMPLETE message");
          if (onAudioSaved) {
            onAudioSaved({
              success: false,
              error: "No audio data - recording may have failed",
              details: { message: "audioBlob is missing or empty" },
            });
          }
          return;
        }

        const liveTranscriptionEnabled = !!data.liveTranscription;

        const config = {
          onAudioSaved,
          bucketName,
          tableName,
          supabaseUrl,
          state,
        };

        // Retry mechanism for failed saves
        const maxRetries = 2;
        let retryCount = 0;
        let result = null;
        let lastError = null;

        while (retryCount <= maxRetries) {
          try {
            result = await ExternalRecordingService.processCompletedRecording(
              data,
              config,
            );

            // If successful or permanently failed (not a retryable error), break
            if (result.success || !isRetryableError(result.error)) {
              break;
            }

            lastError = result.error;
            retryCount++;
            console.log(`⚠️ [PARENT-WINDOW] Retry ${retryCount}/${maxRetries} for audio save, error: ${result.error}`);

            if (retryCount <= maxRetries) {
              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            }
          } catch (processError) {
            lastError = processError.message;
            console.error(`❌ [PARENT-WINDOW] Error processing recording (attempt ${retryCount + 1}):`, processError);
            retryCount++;
            if (retryCount <= maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            }
          }
        }

        if (!result?.success) {
          console.error(
            "❌ [PARENT-WINDOW] Error processing recording after retries:",
            lastError || result?.error,
          );
          // Notify the external window of the failure so it can show appropriate UI
          if (data.sessionId && window.opener) {
            window.opener.postMessage({
              type: 'AUDIO_SAVE_FAILED',
              sessionId: data.sessionId,
              patientId: data.patientId,
              error: lastError || result?.error || 'Unknown error',
              retryAttempts: retryCount,
            }, '*');
          }
        } else if (result.audioUrl && liveTranscriptionEnabled) {
          const transcriptSaveResult = await saveLiveTranscriptDirectly({
            patientId: data.patientId,
            patientName: data.patientName || state?.patient_name,
            transcriptText: data.liveTranscriptText || "",
            duration: data.duration || 0,
            audioUrl: result.audioUrl,
            sessionId: data.sessionId,
          });

          if (!transcriptSaveResult.success) {
            console.warn(
              "⚠️ [PARENT-WINDOW] Live transcript auto-save failed after audio upload:",
              transcriptSaveResult.error,
            );
            if (event.source) {
              event.source.postMessage(
                { type: "LIVE_TRANSCRIPT_SAVED", success: false, error: transcriptSaveResult.error, autoSaved: true },
                "*",
              );
            }
          } else if (event.source) {
            event.source.postMessage(
              { type: "LIVE_TRANSCRIPT_SAVED", success: true, autoSaved: true },
              "*",
            );
          }
        }
      } else if (data.type === "AUDIO_RECORDING_BACKUP") {
        console.log("💾 [PARENT-WINDOW] Processing periodic backup:", {
          backupNumber: data?.backupNumber,
          duration: data?.duration,
          timestamp: data?.timestamp,
          hasTranscription: !!data?.liveTranscriptText,
        });

        try {
          // Queue the transcript snapshot FIRST. It is serialized with every
          // other transcript save, and must not wait behind the audio upload
          // (which can take seconds for long recordings).
          if (data.liveTranscriptText && data.sessionId) {
            if (transcriptionDataRef.current?.sessionId === data.sessionId) {
              transcriptionDataRef.current = {
                ...transcriptionDataRef.current,
                transcriptText: data.liveTranscriptText,
                duration: data.duration || transcriptionDataRef.current.duration,
                recordingState: "recording",
                capturedAt: data.timestamp
                  ? new Date(data.timestamp).getTime() || Date.now()
                  : Date.now(),
              };
            }
            backupTranscriptFromMessage(data, data.liveTranscriptText, "recording")
              .then((transcriptionResult) => {
                if (transcriptionResult.success) {
                  console.log(
                    "📝 [PARENT-WINDOW] Transcription backup saved:",
                    transcriptionResult,
                  );
                } else {
                  console.warn(
                    "📝 [PARENT-WINDOW] Transcription backup failed:",
                    transcriptionResult.error,
                  );
                }
              })
              .catch((transcriptionError) => {
                console.error(
                  "❌ [PARENT-WINDOW] Error saving transcription backup:",
                  transcriptionError,
                );
              });
          }

          const backupStatusForAudioNumber = getBackupStatus();
          const pathAudioSlot =
            backupStatusForAudioNumber?.pathInfo?.audioNumber;
          const periodicPayload = {
            ...data,
            audioNumber:
              data.audioNumber ??
              data.audio_number ??
              pathAudioSlot ??
              null,
          };

          // Process audio backup (clinical audio # must match transcription-backups)
          const backupResult =
            await ExternalRecordingService.processPeriodicBackup(
              periodicPayload,
              {
                bucketName,
                supabaseUrl,
                state,
              },
            );

          if (backupResult.success) {
            console.log(
              "💾 [PARENT-WINDOW] Audio backup saved successfully:",
              backupResult,
            );
          } else {
            console.warn(
              "💾 [PARENT-WINDOW] Audio backup save failed:",
              backupResult.error,
            );
          }

          // Transcript snapshot was already queued above (before the audio
          // upload) so a slow audio upload cannot delay or reorder it.
        } catch (backupError) {
          console.error(
            "❌ [PARENT-WINDOW] Error processing periodic backup:",
            backupError,
          );
        }
      } else if (data.type === "AUDIO_SAVED_SUCCESS") {
        // Handle direct upload success from external window
        // The external window already uploaded the audio. If it also updated the DB
        // directly, only refresh parent UI and avoid creating duplicate entries.
        console.log("🎤 [PARENT-WINDOW] Processing direct upload success:", {
          audioUrl: data?.finalAudioUrl || data?.audioUrl,
          patientId: data?.patientId,
          wasDirectUpload: data?.wasDirectUpload,
          sessionId: data?.sessionId,
          databaseUpdatedDirectly: data?.databaseUpdatedDirectly,
        });

        try {
          if (data?.databaseUpdatedDirectly) {
            // The popup writes the live transcript itself right after the
            // audio row. When it reports that write failed (or a segment save
            // predates that report), append it from here — dedupe by
            // session_id makes this safe to attempt.
            if (
              data.liveTranscription &&
              data.liveTranscriptSaved === false &&
              (data.liveTranscriptText || "").trim()
            ) {
              const fallbackSave = await saveLiveTranscriptDirectly({
                patientId: data.patientId,
                patientName: data.patientName || state?.patient_name,
                audioUrl: data.finalAudioUrl || data.audioUrl,
                transcriptText: data.liveTranscriptText || "",
                duration: data.duration || 0,
                sessionId: data.sessionId,
              });
              console.log(
                "🎤 [PARENT-WINDOW] Live transcript fallback save after popup failure:",
                fallbackSave,
              );
              if (event.source && typeof event.source.postMessage === "function") {
                event.source.postMessage(
                  {
                    type: "LIVE_TRANSCRIPT_SAVED",
                    success: !!fallbackSave.success,
                    error: fallbackSave.error,
                    autoSaved: true,
                    sessionId: data.sessionId,
                  },
                  "*",
                );
              }
            }

            window.dispatchEvent(
              new CustomEvent("audioRecordingComplete", {
                detail: {
                  success: true,
                  audioUrl: data.finalAudioUrl || data.audioUrl,
                  patientId: data.patientId,
                  wasDirectUpload: true,
                  databaseUpdatedDirectly: true,
                },
              }),
            );

            if (onAudioSaved) {
              onAudioSaved({
                success: true,
                audioUrl: data.finalAudioUrl || data.audioUrl,
                patientId: data.patientId,
                wasDirectUpload: true,
                databaseUpdatedDirectly: true,
                sessionId: data.sessionId || null,
                liveTranscriptText: data.liveTranscriptText || "",
                generateAiRecap: data.generateAiRecap ?? true,
                duration: data.duration || 0,
                timestamp: data.timestamp || new Date().toISOString(),
              });
            }
            return;
          }

          const supabase = await getSupabase();
          // The row's schema, not the viewer's — they differ for a chart opened
          // through the cross-schema Providers dropdown, and this write targets
          // that row by id. The popup reports the schema it uploaded under.
          const schemaName =
            resolveRecorderSchema({
              chartSchema: data?.storageSchemaName,
              state,
              homeSchema: await getUserSchema(),
            }) || "public";
          const table = tableForPatientId(data.patientId) || "audio_notes";

          // Build audio data object similar to what processCompletedRecording creates
          const audioData = {
            url: data.finalAudioUrl || data.audioUrl,
            duration: data.duration || 0,
            timestamp: data.timestamp || new Date().toISOString(),
            sessionId: data.sessionId,
          };

          // Update database entry with the new audio
          const updateResult = await ExternalRecordingService.updateRecordingDatabaseEntry(
            {
              patientId: data.patientId,
              audioData: audioData,
              supabase,
              schemaName,
              tableName: table,
              bucketName,
              supabaseUrl,
            }
          );

          if (updateResult.success) {
            console.log("🎤 [PARENT-WINDOW] Database updated with direct upload audio:", updateResult);
            
            // Also save live transcript directly so it survives parent route unmounts
            if (data.liveTranscription) {
              const transcriptSaveResult = await saveLiveTranscriptDirectly({
                patientId: data.patientId,
                patientName: data.patientName || state?.patient_name,
                audioUrl: data.finalAudioUrl || data.audioUrl,
                transcriptText: data.liveTranscriptText || "",
                duration: data.duration || 0,
                sessionId: data.sessionId,
              });

              if (!transcriptSaveResult.success) {
                console.warn(
                  "⚠️ [PARENT-WINDOW] Live transcript auto-save failed on direct upload:",
                  transcriptSaveResult.error,
                );
                if (event.source) {
                  event.source.postMessage(
                    { type: "LIVE_TRANSCRIPT_SAVED", success: false, error: transcriptSaveResult.error, autoSaved: true },
                    "*",
                  );
                }
              } else if (event.source) {
                event.source.postMessage(
                  { type: "LIVE_TRANSCRIPT_SAVED", success: true, autoSaved: true },
                  "*",
                );
              }
            }

            // Dispatch event to notify parent components
            window.dispatchEvent(
              new CustomEvent("audioRecordingComplete", {
                detail: {
                  success: true,
                  audioUrl: data.finalAudioUrl || data.audioUrl,
                  patientId: data.patientId,
                  wasDirectUpload: true,
                },
              }),
            );

            if (onAudioSaved) {
              onAudioSaved({
                success: true,
                audioUrl: data.finalAudioUrl || data.audioUrl,
                patientId: data.patientId,
                wasDirectUpload: true,
                sessionId: data.sessionId || null,
                liveTranscriptText: data.liveTranscriptText || "",
                generateAiRecap: data.generateAiRecap ?? true,
                duration: data.duration || 0,
                timestamp: data.timestamp || new Date().toISOString(),
              });
            }
          } else {
            console.error("❌ [PARENT-WINDOW] Database update failed for direct upload:", updateResult.error);
            if (onAudioSaved) {
              onAudioSaved({
                success: false,
                error: updateResult.error || "Failed to update database",
                patientId: data.patientId,
              });
            }
          }
        } catch (error) {
          console.error("❌ [PARENT-WINDOW] Error processing AUDIO_SAVED_SUCCESS:", error);
          if (onAudioSaved) {
            onAudioSaved({
              success: false,
              error: error.message || "Unknown error",
              patientId: data?.patientId,
            });
          }
        }
      } else if (data.type === "EXPORT_TO_PDF") {
        console.log("📄 [PARENT-WINDOW] Processing PDF export request:", {
          patientId: data?.patientId,
          patientName: data?.patientName,
          duration: data?.duration,
          timestamp: data?.timestamp,
        });

        // Trigger PDF export in parent window
        if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("exportToPDF", {
              detail: {
                patientId: data.patientId,
                patientName: data.patientName,
                audioBlob: data.audioBlob,
                duration: data.duration,
                timestamp: data.timestamp,
                scheduledDate: data.scheduledDate,
                liveTranscription: data.liveTranscription,
                liveFactExtraction: data.liveFactExtraction,
              },
            }),
          );
        }
      } else if (data.type === "REQUEST_TRANSCRIPTION") {
        console.log("🎤 [PARENT-WINDOW] Processing transcription request:", {
          audioUrl: data?.audioUrl,
          duration: data?.duration,
          timestamp: data?.timestamp,
        });

        // Trigger transcription request in parent window
        if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("requestTranscription", {
              detail: {
                audioUrl: data.audioUrl,
                duration: data.duration,
                timestamp: data.timestamp,
                audioFileName: data.audioFileName,
                audioSource: data.audioSource,
                audio_url: data.audio_url || data.audioUrl,
                /** Popup opened by RecordingBubble is not on ExternalRecordingPage ref — use this for replies. */
                sourceWindow: event.source ?? null,
              },
            }),
          );
        }
      } else if (data.type === "SAVE_LIVE_TRANSCRIPT") {
        console.log(
          "🎤 [PARENT-WINDOW] Processing save live transcript request:",
          {
            audioUrl: data?.audioUrl,
            transcriptTextLength: data?.transcriptText?.length,
            timestamp: data?.timestamp,
            alreadySaved: !!data?.alreadySaved,
          },
        );

        // The popup already wrote the row itself: only refresh the parent UI.
        // Dispatching saveLiveTranscript here inserted a second copy.
        if (data.alreadySaved) {
          window.dispatchEvent(new CustomEvent("transcriptionSaved"));
          if (event.source && typeof event.source.postMessage === "function") {
            event.source.postMessage(
              { type: "LIVE_TRANSCRIPT_SAVED", success: true, sessionId: data.sessionId },
              "*",
            );
          }
        } else if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("saveLiveTranscript", {
              detail: {
                audioUrl: data.audioUrl,
                transcriptText: data.transcriptText,
                duration: data.duration,
                timestamp: data.timestamp,
                sessionId: data.sessionId,
              },
            }),
          );
        }
      } else if (data.type === "LIVE_TRANSCRIPT_UPDATE") {
        // Handle live transcript updates from external window
        console.log("🎤 [PARENT-WINDOW] Processing live transcript update:", {
          textLength: data?.text?.length || 0,
          textPreview: data?.text?.substring(0, 50) || "(empty)",
        });

        // Update the transcription data ref so periodic backups get the latest
        // text. `saveText` is the popup's canonical merged transcript (same
        // text it persists on stop/pause/backup); `text` is the display
        // version. A frame tagged with another session (stale popup after
        // Save & continue) must not overwrite this session's snapshot.
        const frameSession = data.sessionId || null;
        const refSession = transcriptionDataRef.current?.sessionId || null;
        if (!frameSession || !refSession || frameSession === refSession) {
          transcriptionDataRef.current = {
            ...transcriptionDataRef.current,
            transcriptText: data.saveText || data.text || "",
            duration: data.duration || transcriptionDataRef.current.duration,
            recordingState: "recording",
            sessionId: refSession || frameSession,
            capturedAt: data.timestamp
              ? new Date(data.timestamp).getTime() || Date.now()
              : Date.now(),
          };
        }

        // Dispatch event to update live transcript in parent window components
        if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("liveTranscriptUpdate", {
              detail: {
                text: data.text || "",
                timestamp: data.timestamp || new Date().toISOString(),
              },
            }),
          );
        }
      } else if (data.type === "AUDIO_CHUNK_FOR_TRANSCRIPTION") {
        // Forward audio chunk from external window to WebSocket for live transcription
        // This ensures system audio (merged with mic) is transcribed via WebSocket
        console.log("🎤 [PARENT-WINDOW] Processing audio chunk for WebSocket transcription:", {
          audioSize: data?.audioBlob?.size || data?.audioData?.byteLength || 0,
          sessionId: data?.sessionId,
          includeSystemAudio: data?.includeSystemAudio,
        });

        // Dispatch custom event with audio chunk for components with WebSocket access
        if (window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent("externalAudioChunkForTranscription", {
              detail: {
                audioBlob: data.audioBlob || null,
                audioData: data.audioData || null,
                sessionId: data.sessionId,
                patientId: data.patientId,
                includeSystemAudio: data.includeSystemAudio,
                timestamp: data.timestamp || new Date().toISOString(),
              },
            }),
          );
          console.log("🎤 [PARENT-WINDOW] Dispatched externalAudioChunkForTranscription event");
          
          // Send ACK back to external window to confirm receipt
          if (window.opener && data.sessionId) {
            window.opener.postMessage({
              type: 'AUDIO_CHUNK_FOR_TRANSCRIPTION_ACK',
              sessionId: data.sessionId,
              timestamp: data.timestamp || new Date().toISOString(),
            }, '*');
            console.log("🎤 [PARENT-WINDOW] Sent AUDIO_CHUNK_FOR_TRANSCRIPTION_ACK to external window");
          }
        }
      } else {
        console.log("🎤 [PARENT-WINDOW] Ignored message type:", data?.type);
      }
    },
    [
      onAudioSaved,
      bucketName,
      tableName,
      supabaseUrl,
      state,
      saveLiveTranscriptDirectly,
      currentTranscriptionSession,
      backupTranscriptFromMessage,
    ],
  );

  return {
    handleExternalAudioComplete,
    createStandardizedFilename,
    // Export backup utilities for use by parent components
    initBackupSession,
    startPeriodicBackup,
    stopPeriodicBackup,
    saveTranscriptionBackup,
    ensureBackupBucket,
    endBackupSession,
    getBackupStatus,
  };
};
